import { defineMessages } from 'react-intl';

export const dataTableMessages = defineMessages({
  // Accessible name
  // ---------------
  dataTableLabel: {
    defaultMessage: 'Data table',
    id: 'OmNf7ak/Kcv7wP3u',
    description:
      'Default accessible name for the DataTable component when no aria-label or aria-labelledby is provided.',
  },

  // Selection messages
  // ------------------
  selectAllTooltip: {
    defaultMessage:
      '{nextSelection, select, select {Select} deselect {Deselect} other{}} all rows {scope, select, page {on current page} all {in the table} other {}}',
    id: 'KxAo/sbsalGLDonj',
    description: 'Toggle selection for all rows on the page',
  },
  selectRowCheckbox: {
    defaultMessage: 'Toggle row selection for row with id "{id}".',
    id: 'FJE1QgzcKO8lKmt4',
    description: 'Toggle selection for an individual row',
  },
  selectedTotalRows: {
    defaultMessage: 'selected',
    id: 'xFQW8ah15nm5hjiI',
    description: 'Message to display for the total count of selected rows.',
  },
  rowSelectionAnnouncement: {
    defaultMessage:
      '{count, plural, =0 {No rows selected} one {# row selected} other {# rows selected}}',
    id: 'nt9FnUYyS/Otapls',
    description:
      'Screen reader announcement for the current number of selected rows after a selection change.',
  },

  // Data state messages
  // -------------------
  noDataAvailable: {
    defaultMessage: 'No data available.',
    id: 'VkfutNWZVq0loZNC',
    description: 'Message to display when no data is available',
  },
  noColumnsAvailable: {
    defaultMessage: 'No columns have been added.',
    id: 'W8hOQDfAAXoNw46k',
    description: 'Message to display when no table columns are configured.',
  },
  noColumnsVisible: {
    defaultMessage: 'All table columns are hidden.',
    id: '+Vxz7rIFW86zQsz8',
    description:
      'When all columns are hidden, this is shown in the main table area.',
  },
  loadingData: {
    defaultMessage: 'Loading data',
    id: 'EWfCcasdyt/QUnjk',
    description: 'Label for DataTable loading state.',
  },

  // Row actions
  // -----------
  rowActionsTrigger: {
    defaultMessage: 'Show more actions',
    id: 'L/D6X7Ee5PYWzl3i',
  },
  rowDetailsTrigger: {
    defaultMessage:
      '{isDetailsOpen, select, true {Collapse} other {Expand}} row with id {id}',
    id: '8a6rHEksmq2fZCQl',
    description: 'Aria label used for data table expandable row trigger.',
  },
  rowDragAndDropLabel: {
    defaultMessage: 'Drag to order row "{rowId}".',
    id: 'CrdM30eeqaF3mlZr',
    description: 'Label for the row drag handle.',
  },
  rowDragAndDropSortedTooltip: {
    defaultMessage:
      'Clear {sortDirection, select, ascending {ascending} descending {descending} other {}} sort order to drag and drop rows',
    id: 'miecKI74dxONRE98',
    description:
      'Tooltip shown on a disabled row drag handle when reordering is unavailable because the table is sorted.',
  },
  interactiveRowTrigger: {
    defaultMessage:
      '{isActivated, select, true {Deactivate} other {Activate}} row with id "{rowId}".',
    id: 'an4qwOaAQd8t60m5',
    description: 'Aria label for the interactive row trigger button.',
  },
  interactiveRowRoleDescription: {
    defaultMessage: 'interactive row',
    id: 'SZrN/y5KM3ow7Jed',
    description:
      'Aria role description for the interactive row trigger button.',
  },

  // Pagination messages
  // -------------------
  paginationRowsPerPage: {
    defaultMessage: 'rows per page',
    id: 'lu2hmVHtfcnVRPtz',
    description: 'Text to display when choosing the number of rows per page.',
  },
  paginationPageSelection: {
    defaultMessage: 'Page',
    id: 'LCNiBr0aBntMq391',
    description: 'Label for page selection dropdown.',
  },
  paginationTotalPages: {
    defaultMessage: 'of {totalPages}',
    id: 'HzhWa5UT4EGJIstf',
    description: 'Text displaying the amount of pages.',
  },
  paginationSinglePageInfo: {
    defaultMessage: '1 of 1',
    id: '91t8w7tO+iaic/Z+',
    description:
      'Text displaying the page indicator when there is only 1 page in the table',
  },
  paginationPrevPage: {
    defaultMessage: 'Go to previous page.',
    id: 'VoqQWp9yEM0QM8eF',
    description: 'Aria label to add to the `previous` button',
  },
  paginationNextPage: {
    defaultMessage: 'Go to next page.',
    id: 'KAAzM9PVtiJno9ks',
    description: 'Aria label to add to the `next` button',
  },
  selectPageSize: {
    defaultMessage: 'Select page size',
    id: 'tT1Q91yIBC2+wpsM',
    description: 'Aria-label for selecting current page size.',
  },
  selectPage: {
    defaultMessage: 'Select current page',
    id: 'Psa1yEbRJtw20yK3',
    description: 'Aria-label for selecting current page index.',
  },

  // Column movement and pinning
  // ---------------------------
  moveColumnLeftLabel: {
    defaultMessage: 'Move left',
    id: 'Hb5phHuz1zWpI7/Z',
    description:
      'Text in the column action item for changing the column order, moving column to the left.',
  },
  moveColumnRightLabel: {
    defaultMessage: 'Move right',
    id: 'RxaXcljpkrMAErS9',
    description:
      'Text in the column action item for changing the column order, moving column to the right.',
  },
  pinColumnLabel: {
    defaultMessage:
      '{isUnpinned, select, true {Pin column} other {Pin/Unpin column}}',
    id: 'ZsZq59BFxrIW5JzO',
    description:
      'Tooltip for pinning or unpinning the column depending on its current state.',
  },
  pinColumnLeftLabel: {
    defaultMessage: 'Pin left',
    id: 'z6Q99ieTCziyTcca',
    description: 'Text in the menu item for pinning the column to the left.',
  },
  pinColumnRightLabel: {
    defaultMessage: 'Pin right',
    id: 'B0JVHiCeA3v550+2',
    description: 'Text in the menu item for pinning the column to the right.',
  },
  unpinColumnLabel: {
    defaultMessage: 'Unpin',
    id: 'ZGvbau7roOR3vlCG',
    description: 'Text in the menu item for unpinning the column.',
  },
  resizeColumnLabel: {
    defaultMessage: 'Change size for "{column}" column',
    id: 'grMCOLy6cDAqdO7V',
    description: 'Aria label for DataTable header resize handler',
  },

  // Font style
  // ----------
  fontStyleCodeLabel: {
    defaultMessage: 'Change font: monospace',
    id: 'fE+5JaLlWXQ3dlNm',
    description: 'Set the font style of the column text.',
  },
  fontStyleTextLabel: {
    defaultMessage: 'Change font: default',
    id: 'Xug47ebOKpTzuLj1',
    description: 'Set the font style of the column text.',
  },

  // Column actions
  // --------------
  hideColumnLabel: {
    defaultMessage: 'Hide column',
    id: 'LQ8VkjDPP6CnNrhf',
    description: 'Text in the column action item for hiding the column.',
  },
  enableLineWrapLabel: {
    defaultMessage: 'Enable line wrap',
    id: 'yRTBrmuLLhgDytyG',
    description:
      'Text in the column action item for enabling line wrap for the column.',
  },
  disableLineWrapLabel: {
    defaultMessage: 'Disable line wrap',
    id: 'dxcsnLOTenNXGZkn',
    description:
      'Text in the column action item for disabling line wrap for the column.',
  },
  downloadButtonLabel: {
    defaultMessage: 'Download table data',
    id: 'n17DpsKydU3rdqK9',
    description: 'Tooltip information for the download table button.',
  },
  downloadingButtonLabel: {
    defaultMessage: 'Downloading table data',
    id: 'YqEOqKR9iZt3qY0t',
    description:
      'Tooltip information for the download table button while download is in progress.',
  },

  // Cell and column actions
  // ------------
  cellActionLabel: {
    defaultMessage: 'Show cell actions',
    id: 'GXANZLEVr4dA1bxU',
    description: 'Aria label for DataTable cell actions trigger',
  },
  copyValueCellAction: {
    defaultMessage: 'Copy value',
    id: '47orYkGnlAr7+sPw',
    description:
      'Text in the cell action item for copying the content of the table cell to the clipboard.',
  },
  columnActionLabel: {
    defaultMessage: 'Show column actions for "{column}"',
    id: 'l99kSRjiLY8vJnRN',
    description: 'Aria label for DataTable column actions trigger',
  },

  // Download messages
  // -----------------
  downloadTable: {
    defaultMessage: 'Download CSV',
    id: '/DJNcsGLrHi/d+Cy',
    description: 'Label for the download group in the table actions menu',
  },
  downloadAll: {
    defaultMessage: 'All',
    id: 'tDvUEsdbUj+XNZTR',
    description: 'Label for downloading all table rows',
  },
  downloadPage: {
    defaultMessage: 'Current page',
    id: 'n3kHNMq0wIDbAVfm',
    description: 'Label for downloading current page rows',
  },
  downloadSelected: {
    defaultMessage: 'Selected rows',
    id: 'CZzImkwrQi9rMTqS',
    description: 'Label for downloading only the selected rows',
  },
  downloadRowsCount: {
    defaultMessage: '{noOfRows, plural, one {# row} other {# rows}}',
    id: 'g2fLjUNdQ/qPFLMn',
    description: 'Row count shown alongside each download option',
  },

  // Toolbar / Column settings
  // -------
  toolbarButtonLabel: {
    defaultMessage: 'More options',
    id: 'wJb5aQ8q06TidoIt',
    description: 'Tooltip information for the toolbar menu button',
  },
  columnSettingsTitle: {
    defaultMessage: 'Columns',
    id: 'tznlZbNaiUJ1dPqb',
    description: 'Column settings modal title',
  },
  columnSettingsTooltip: {
    defaultMessage: 'Column settings',
    id: 'DoSCFZmkm4Wgk+9J',
    description: 'Tooltip information for the column settings button',
  },
  columnSettingsButtonDefault: {
    defaultMessage: 'Columns',
    id: 'F78crM1uQumIOJdh',
    description: 'Column settings button default text',
  },
  columnSettingsButtonVisibility: {
    defaultMessage:
      '{hiddenColumnCount} {hiddenColumnCount, plural, one {column} other {columns}} hidden',
    id: 'aqGvcDl8n+XdhJiq',
    description: 'Column visibility button text',
  },
  showAll: {
    defaultMessage: 'Show all',
    id: 'Pejm0KE1UcIcJEN7',
    description: 'Show all columns button label in the column settings modal.',
  },
  hideAll: {
    defaultMessage: 'Hide all',
    id: '8jgPPxKxjmqUGMNe',
    description: 'Hide all columns button label in the column settings modal.',
  },
  listHiddenColumns: {
    defaultMessage: 'List hidden columns ({count})',
    id: 'tF7SFYalNuZIFV5Z',
    description:
      'Label for the switch that controls whether hidden columns appear in the column settings list. count is the number of currently hidden columns.',
  },
  columnSettingsShowingCount: {
    defaultMessage: 'Showing {visible} out of {total}',
    id: 'sSf0lHN0h4mZD8r2',
    description:
      'Shows how many columns are currently visible out of the total in the column settings modal.',
  },
  allColumnsHiddenTitle: {
    defaultMessage: 'All columns hidden',
    id: 'gJo6Pm1rBw056PsG',
    description:
      'Heading of the empty state shown in the column settings modal when all columns are hidden.',
  },
  allColumnsHiddenDescription: {
    defaultMessage: "Enable 'List hidden columns' to see them.",
    id: 'Riz3wAzTiv590+pg',
    description:
      "Body text of the empty state in the column settings modal when all columns are hidden and the 'list hidden columns' switch is off.",
  },
  listHiddenColumnsAction: {
    defaultMessage: 'List hidden columns',
    id: 'QcvItWP6U/6ryl2u',
    description:
      "Label for the CTA button in the empty state that enables the 'list hidden columns' switch.",
  },
  searchBarPlaceholder: {
    defaultMessage: 'Search',
    id: 'TIVnKWerQ6BuCdAB',
    description: 'Search placeholder',
  },
  cancelButtonText: {
    defaultMessage: 'Cancel',
    id: 'euRey+Fz9NxskAuO',
    description: 'Cancel button text',
  },
  applyButtonText: {
    defaultMessage: 'Apply',
    id: 'oVgkJujKiRvYqIFQ',
    description: 'Apply button text',
  },
  noFilterResultsTitle: {
    defaultMessage: 'No results found',
    id: 'xbU5jK0wrKGToY9R',
    description:
      'Heading shown when the column settings search filter produces no results.',
  },
  filterResultsCount: {
    defaultMessage:
      '{count, plural, one {# column found} other {# columns found}}',
    id: 'qZC6v+YzE6aXQpMY',
    description:
      'Screen reader announcement of the number of leaf columns matching the column settings search filter.',
  },
  showingAllColumns: {
    defaultMessage:
      '{count, plural, one {Showing # column} other {Showing all # columns}}',
    id: 'b+Rx1jqDkljv85d6',
    description:
      'Screen reader announcement when the column settings search is cleared, showing the total number of available columns.',
  },
  noFilterResultsDescription: {
    defaultMessage:
      'Try clearing the search and adding different search words.',
    id: '/cHIv7hlXvB+krTT',
    description:
      'Description shown below the no-results heading in column settings search.',
  },
  clearSearchAction: {
    defaultMessage: 'Clear search',
    id: 'JPnuokqbGzUhWJWE',
    description:
      'Button label that clears the search filter in the column settings modal.',
  },
  resetColumnSettingsButtonText: {
    defaultMessage: 'Reset to default',
    id: 'A1+FGKx7bONltqJV',
    description: 'Resets column settings to default',
  },
  resetSuccessful: {
    defaultMessage: 'Column settings are reset to default.',
    id: 'TO+JQQr/hhHRjVL4',
    description:
      'Status message that resetting column settings to default was successful.',
  },
  columnSettingsDragAndDropLabel: {
    defaultMessage: 'Drag to order column "{columnId}".',
    id: 'WGVOUOAZiBC2rWlk',
    description: 'Label for the column drag handle in the column settings.',
  },
  columnSettingsSectionPinnedLeft: {
    defaultMessage: 'Pinned to left',
    id: 'FWPcCUBJ8OXZAxdJ',
    description:
      'Section header for columns pinned to the left in the column settings modal.',
  },
  columnSettingsSectionUnpinned: {
    defaultMessage: 'Unpinned',
    id: 'ml+Pyzyh+p8TWy3Z',
    description:
      'Section header for columns that are not pinned in the column settings modal.',
  },
  columnSettingsSectionPinnedRight: {
    defaultMessage: 'Pinned to right',
    id: 'DXbY+p0kaQthdZgP',
    description:
      'Section header for columns pinned to the right in the column settings modal.',
  },
  columnHiddenChipLabel: {
    defaultMessage: 'Hidden',
    id: 'BqRVinIeQxz83n1M',
    description:
      'Label of the chip shown next to a hidden column in the column settings modal.',
  },
  toggleColumnVisibilityLabel: {
    defaultMessage: '{isHidden, select, true {Show} other {Hide}} column',
    id: 'ZhUT5iq6+B7hRPfb',
    description:
      'Tooltip for the button toggling the visibility of a column in the column settings.',
  },
  toggleColumnVisibilityAriaLabel: {
    defaultMessage: '{isHidden, select, true {Show} other {Hide}} {columnName}',
    id: 'MJmQVUKWduRqcQ2F',
    description:
      'Aria label for the button toggling the visibility of a column in the column settings. columnName is the display label of the column.',
  },
  columnVisibilityDisabledLabel: {
    defaultMessage:
      "Column can't be {isHidden, select, true {shown} other {hidden}}",
    id: 'UZ1xW95v+ePFi3HE',
    description:
      "Tooltip for the visibility toggle of a column whose visibility can't be changed.",
  },
  columnVisibilityDisabledAriaLabel: {
    defaultMessage:
      "{columnName} can't be {isHidden, select, true {shown} other {hidden}}",
    id: 'QsCiOjrrvWmpOx/R',
    description:
      "Aria label for the visibility toggle of a column whose visibility can't be changed. columnName is the display label of the column.",
  },
  visibleColumnsMinError: {
    defaultMessage: 'At least 1 column must be shown.',
    id: 'FIMM9WXC8Kwieah3',
    description:
      'Error message shown in column settings modal when the user tries to apply changes with no visible columns.',
  },
  visibleColumnsWarning: {
    defaultMessage:
      'Displaying more than {limit} {limit, plural, one {column} other {columns}} may slow down the table.',
    id: 'XujtZVmxX3xAIEud',
    description:
      'Warning message shown in column settings modal when the number of visible columns exceeds the warning threshold.',
  },
  visibleColumnsError: {
    defaultMessage:
      'You can show up to {limit} {limit, plural, one {column} other {columns}} at a time.',
    id: 'DJH6Mo6y4LT1rFxc',
    description:
      'Error message shown in column settings modal when the number of visible columns exceeds the error threshold.',
  },

  // Sorting
  // -------
  dragAndDropLabel: {
    defaultMessage: 'Drag to reorder "{column}" column',
    id: 'RicigT+v0BvVWaSt',
    description: 'Aria label for DataTable draggable header',
  },
  sortingLabel: {
    defaultMessage:
      'Change sorting for "{column}" column to {direction, select, asc {ascending} desc {descending} other {unset}}',
    id: 'mTmhCH1Z7z3TY8Jo',
    description: 'Aria label for DataTable header button that toggles sorting',
  },
  sortableColumnIcon: {
    defaultMessage: 'Icon indicating that the column is sortable',
    id: 'Ah9w1glbeyevy7N+',
    description:
      'Aria-label for sortable columns that are not yet sorted in any direction.',
  },
  sortingDragAndDropLabel: {
    defaultMessage:
      'Change sorting for "{column}" column to {direction, select, asc {ascending} desc {descending} other {unset}} or drag to reorder "{column}" column.',
    id: 'kq1Ips7kEIlJ2yFv',
    description:
      'Aria label for DataTable header button that toggles sorting and allows for drag and drop column reordering.',
  },
  sortingStateLabel: {
    defaultMessage:
      'Sorted {direction, select, asc {ascending} desc {descending} other {unsorted}}.',
    id: '6Ut4st/DFcfYJorv',
    description:
      'Aria description announcing the current sorting state of the column header.',
  },
  sortingStateWithPriorityLabel: {
    defaultMessage:
      'Sorted {direction, select, asc {ascending} desc {descending} other {unsorted}}, sort priority {priority} of {total}.',
    id: '4NCInqG2qgKe0TSA',
    description:
      'Aria description announcing the current sorting state and priority in multi-column sorting.',
  },

  // Sorting column actions menu
  // ---------------------------
  sortAscending: {
    defaultMessage: 'Sort ascending',
    id: '2w4ytnIw5OSGcvDR',
    description:
      'Column action menu item for sorting a column in ascending order (single sort).',
  },
  sortDescending: {
    defaultMessage: 'Sort descending',
    id: 'BxL/OHRXGQ4NxkRA',
    description:
      'Column action menu item for sorting a column in descending order (single sort).',
  },
  addAsAscending: {
    defaultMessage: 'Add as ascending',
    id: 'oDh+EhNRRqQQkNAS',
    description:
      'Column action menu item for multi-sorting a column in ascending order.',
  },
  addAsDescending: {
    defaultMessage: 'Add as descending',
    id: 'zgKXUXme0dmc9SsD',
    description:
      'Column action menu item for multi-sorting a column in descending order.',
  },
  changeToAscending: {
    defaultMessage: 'Change to ascending',
    id: 'iO0lZy6i8mjT0jzt',
    description:
      'Column action menu item for multi-sorting a column in ascending order when the column is already part of a multi-sort.',
  },
  changeToDescending: {
    defaultMessage: 'Change to descending',
    id: 'b5WOrn1oLsbJsvD8',
    description:
      'Column action menu item for multi-sorting a column in descending order when the column is already part of a multi-sort.',
  },
  clearSorting: {
    defaultMessage: 'Clear sorting',
    id: 'F7t2bxwXuoFZkUcl',
    description:
      'Column action menu item for removing sorting for this column.',
  },
  multiColumnSortingLabel: {
    defaultMessage: 'Multi-column sorting',
    id: 'Mh5VCfdpjmGhMnOB',
    description:
      'Label for the multi-column sorting group in the column actions menu.',
  },
  sortSubmenuTrigger: {
    defaultMessage: 'Sort',
    id: '5eX0f5J5OWwoQNSw',
    description:
      'Sub-menu trigger label for sorting actions in the column actions menu.',
  },

  // Built-in column header accessible names
  // -----------------------------------------
  rowSelectionColumnName: {
    defaultMessage: 'Row selection',
    id: 'nwlCPRYNTOEFpb9x',
    description: 'Accessible label for the row selection column header.',
  },
  rowActionsColumnName: {
    defaultMessage: 'Row actions',
    id: 'xRve2qkedJdro00f',
    description: 'Accessible label for the row actions column header.',
  },
  rowOrderColumnName: {
    defaultMessage: 'Row order',
    id: 'aP5zePnHhLCSP5Ar',
    description:
      'Accessible label for the row order drag-and-drop column header.',
  },
  rowDetailsColumnName: {
    defaultMessage: 'Expandable rows',
    id: 'i/cxuYRtkf4OrTvH',
    description: 'Accessible label for the expandable rows column header.',
  },

  // Subrow messages
  // ---------------
  subRowAllTrigger: {
    defaultMessage:
      '{expandState, select, expand {Expand} other {Collapse}} all subrows',
    id: 'YithM+0sfIXQLp9+',
    description: 'Describes the action of the sub rows trigger',
  },
  subRowTrigger: {
    defaultMessage:
      '{expandState, select, expand {Expand} other {Collapse}} subrow with id "{id}".',
    id: 'kCk8ZqQbTzdkw3WN',
    description: 'Describes the action of the sub rows trigger',
  },
});
